import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { listCustomFields } from '../services/customFields/listCustomFields';
import { CustomField } from '../types/custom-fields/types';


const CACHE_EXPIRY_TIME = 60 * 60 * 1000; // 1 hour in milliseconds
const STORAGE_PREFIX = 'chs_cf_cache_';

// Global cache for in-flight requests to prevent duplicate API calls
const inFlightRequests = new Map<string, Promise<CustomField[]>>();

export const useCustomFields = (
  clientId: string | null,
  tableName: string | null
) => {
  const { clientIdPractice } = useAuth();
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolvedClientId = clientId ?? clientIdPractice;

  // Normalize empty strings to null - empty string is falsy and prevents API calls
  const normalizedClientId =
    resolvedClientId && resolvedClientId.trim() !== ''
      ? resolvedClientId
      : null;
  const normalizedTableName =
    tableName && tableName.trim() !== '' ? tableName : null;

  // Create a cache key - only if both values are provided
  const cacheKey =
    normalizedClientId && normalizedTableName
      ? `${normalizedClientId}-${normalizedTableName}`
      : null;

  // Check if we have valid cached data
  const getCachedData = useCallback((): CustomField[] | null => {
    if (!cacheKey) return null;

    try {
      const storageKey = `${STORAGE_PREFIX}${cacheKey}`;
      const cachedItem = localStorage.getItem(storageKey);
      if (!cachedItem) return null;

      const { data, timestamp } = JSON.parse(cachedItem);

      // Check if cache is expired
      if (Date.now() - timestamp > CACHE_EXPIRY_TIME) {
        localStorage.removeItem(storageKey);
        return null;
      }

      return data as CustomField[];
    } catch (e) {
      console.warn('Failed to parse custom fields from localStorage', e);
      return null;
    }
  }, [cacheKey]);

  // Set cached data
  const setCachedData = useCallback(
    (data: CustomField[]) => {
      if (!cacheKey) return;

      try {
        const storageKey = `${STORAGE_PREFIX}${cacheKey}`;
        localStorage.setItem(storageKey, JSON.stringify({
          data,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('Failed to save custom fields to localStorage', e);
      }
    },
    [cacheKey]
  );

  // Fetch custom fields with promise deduplication
  const fetchCustomFields = useCallback(async () => {
    // Use normalized values - if no clientId or tableName, don't fetch
    if (!normalizedClientId || !normalizedTableName) {
      setCustomFields([]);
      setLoading(false);
      setError('Invalid parameters provided');
      return;
    }

    // Check cache first
    const cachedData = getCachedData();
    if (cachedData) {
      setCustomFields(cachedData);
      setLoading(false);
      setError(null);
      return;
    }

    // Check if there's already an in-flight request for this key
    if (inFlightRequests.has(cacheKey!)) {
      setLoading(true);
      try {
        const fields = await inFlightRequests.get(cacheKey!)!;
        setCustomFields(fields);
        setError(null);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to fetch custom fields';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError(null);

    // Create and store the promise for deduplication
    const fetchPromise = listCustomFields(
      normalizedClientId,
      normalizedTableName
    ).then(
      (response) => {
        const fields = response.data.custom_fields || [];
        setCachedData(fields);
        // Remove from in-flight requests
        inFlightRequests.delete(cacheKey!);
        return fields;
      }
    ).catch((err) => {
      // Remove from in-flight requests on error too
      inFlightRequests.delete(cacheKey!);
      throw err;
    });

    inFlightRequests.set(cacheKey!, fetchPromise);

    try {
      const fields = await fetchPromise;
      setCustomFields(fields);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch custom fields';
      setError(errorMessage);
      console.error('Failed to fetch custom fields:', err);
    } finally {
      setLoading(false);
    }
  }, [
    normalizedClientId,
    normalizedTableName,
    cacheKey,
    getCachedData,
    setCachedData,
  ]);

  useEffect(() => {
    if (normalizedClientId && normalizedTableName) {
      fetchCustomFields();
    } else if (!normalizedClientId || !normalizedTableName) {
      // Clear data if no valid parameters
      setCustomFields([]);
      setLoading(false);
      setError(null);
    }
  }, [normalizedClientId, normalizedTableName, fetchCustomFields]);

  // Function to refresh data (useful for force refresh)
  const refresh = useCallback(() => {
    // Clear cache for this key
    if (cacheKey) {
      const storageKey = `${STORAGE_PREFIX}${cacheKey}`;
      localStorage.removeItem(storageKey);
    }
    return fetchCustomFields();
  }, [cacheKey, fetchCustomFields]);

  // Function to clear all cache (useful for logout or major data changes)
  const clearCache = useCallback(() => {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(STORAGE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  }, []);

  return {
    customFields,
    loading,
    error,
    refresh,
    clearCache,
  };
};
